/**
 * Verified Entry API calls
 */

import apiClient from './client';
import {
  VerifiedEntry,
  CreateEntryRequest,
  UpdateEntryRequest,
  EntrySearchParams,
  CreateEntryMediaRequest,
  UpdateEntryMediaRequest,
  EntryMedia,
  CreateMissingEntryRequest,
} from '@/types/api';

/**
 * Get all entries with optional filters
 */
export const getEntries = async (
  params?: EntrySearchParams
): Promise<VerifiedEntry[]> => {
  const response = await apiClient.get<VerifiedEntry[]>('/entries', { params });
  return response.data;
};

/**
 * Get single entry by ID
 */
export const getEntry = async (id: number): Promise<VerifiedEntry> => {
  const response = await apiClient.get<VerifiedEntry>(`/entries/${id}`);
  return response.data;
};

/**
 * Create new entry
 */
export const createEntry = async (data: CreateEntryRequest): Promise<VerifiedEntry> => {
  const response = await apiClient.post<VerifiedEntry>('/entries', data);
  return response.data;
};

/**
 * Create missing entry placeholder
 */
export const createMissingEntry = async (
  data: CreateMissingEntryRequest
): Promise<VerifiedEntry> => {
  const response = await apiClient.post<VerifiedEntry>('/entries/missing', data);
  return response.data;
};

/**
 * Update existing entry
 */
export const updateEntry = async (id: number, data: UpdateEntryRequest): Promise<VerifiedEntry> => {
  const response = await apiClient.put<VerifiedEntry>(`/entries/${id}`, data);
  return response.data;
};

/**
 * Delete entry
 */
export const deleteEntry = async (id: number): Promise<void> => {
  await apiClient.delete(`/entries/${id}`);
};

/**
 * Get media files linked to an entry
 */
export const getEntryMedia = async (entryId: number): Promise<EntryMedia[]> => {
  const response = await apiClient.get<EntryMedia[]>(`/entries/${entryId}/media`);
  return response.data;
};

/**
 * Link media file to entry
 */
export const createEntryMedia = async (
  entryId: number,
  data: CreateEntryMediaRequest
): Promise<EntryMedia> => {
  const response = await apiClient.post<EntryMedia>(`/entries/${entryId}/media`, data);
  return response.data;
};

/**
 * Update entry media link
 */
export const updateEntryMedia = async (
  entryId: number,
  mediaLinkId: number,
  data: UpdateEntryMediaRequest
): Promise<EntryMedia> => {
  const response = await apiClient.put<EntryMedia>(
    `/entries/${entryId}/media/${mediaLinkId}`,
    data
  );
  return response.data;
};

/**
 * Delete entry media link
 */
export const deleteEntryMedia = async (entryId: number, mediaLinkId: number): Promise<void> => {
  await apiClient.delete(`/entries/${entryId}/media/${mediaLinkId}`);
};

/**
 * Batch create entries with media links
 */
export const batchCreateEntries = async (entries: CreateEntryRequest[]): Promise<VerifiedEntry[]> => {
  const response = await apiClient.post<VerifiedEntry[]>('/entries/batch', entries);
  return response.data;
};
